import { View } from 'react-native';
import { twMerge } from 'tailwind-merge';

interface BulletDotProps {
  /** Announced to a screen reader when the row's marker carries meaning. Left
   *  unset the dot is silent, which is what a decorative marker should be. */
  accessibilityLabel?: string;
  /** Extra classes, merged over the defaults. Size and top margin move
   *  together, so a caller changing one should set both. */
  className?: string;
}

/**
 * The marker at the head of a list row.
 *
 * Drawn rather than typed. A bullet character is a glyph, so sizing it means
 * sizing a font, and two rows that want different weight end up on different
 * baselines — which reads as a rendering fault rather than as a signal. A view
 * with fixed dimensions has one geometry whatever sits beside it.
 *
 * Small and dark rather than large and pale. A muted stop was tried first and
 * a washed-out blob is what it gave: at this size a dot has too little area to
 * carry a low-contrast color, so lightening it reads as a rendering artifact
 * rather than as restraint. It takes the body text's own color instead, which
 * is 7.34:1 on the card in light and 9.13:1 in dark, and shrinks until the
 * weight is right. The marker then reads as punctuation belonging to the
 * sentence beside it, which is what it is.
 *
 * Do not tint it to encode a category. The brand ramp is barely saturated, so
 * a tinted dot is perceived as a darker gray rather than as a color, and a
 * code nobody can read is decoration that looks like information.
 *
 * The top margin centers the dot on the first line of body text at the app's
 * default size and leading. Pair a different text size with a different
 * margin.
 */
export function BulletDot({ accessibilityLabel, className }: BulletDotProps) {
  return (
    <View
      className={twMerge(
        'mt-2.5 h-1.5 w-1.5 flex-none rounded-full bg-typography-700',
        className
      )}
      accessible={accessibilityLabel !== undefined}
      accessibilityLabel={accessibilityLabel}
    />
  );
}
